import { useUpdateAiWorkspace } from "@entities/aiWorkspace";
import {
  IAIWorkspace,
  IAIWorkspaceRequest,
} from "@entities/aiWorkspace/lib/types";
import { useBriefDownload } from "@features/UnifiedCampaignSetup/hooks/useBriefDownload";
import { BriefEditorSection } from "@features/UnifiedCampaignSetup/ui/BriefEditorSection";
import { PROJECT_AI_MODULES } from "@pages/AIWorkspace/modules";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import { Collapse } from "antd";
import { formatDistanceToNow } from "date-fns";
import { ArrowRight, Clock } from "lucide-react";
import React, { useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { OverviewMasterPrompts } from "./OverviewMasterPrompts";
import { requestRowCopy } from "./requestRowCopy";
import { creatorsFromRequest } from "./ScriptGenerationTab/creatorsFromRequest";
import { requestIsWorking } from "./sidebarStatus";
import { moduleIdForRequestType } from "./useAiWorkspaceModuleParams";
import styles from "./styles.module.scss";

export const OverviewTab: React.FC<{
  workspace: IAIWorkspace;
  requests: IAIWorkspaceRequest[];
  onOpenRequest: (requestId: string, type: string) => void;
}> = ({ workspace, requests, onOpenRequest }) => {
  const editorRef = useRef<EditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  const [downloadOpen, setDownloadOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusTone, setStatusTone] = useState<"ok" | "err">("ok");

  const { update } = useUpdateAiWorkspace();

  const showStatus = (message: string, tone: "ok" | "err" = "ok") => {
    setStatusTone(tone);
    setStatusMessage(message);
  };

  const { isDownloading, downloadLabel, handleDownloadDoc, handleDownloadPdf } =
    useBriefDownload({
      editorRef,
      campaignName: workspace.name,
      showStatus,
      onMenuClose: () => setDownloadOpen(false),
    });

  const debouncedSave = useDebouncedCallback(() => {
    const html = editorRef.current?.getHTML({ syncTokens: false }) ?? "";
    update({ aiWorkspaceId: workspace._id, overview: html });
  }, 1000);

  const handleEditorChange = () => {
    debouncedSave();
  };

  return (
    <div style={{ marginTop: "16px" }}>
      <section className={styles.editorSection}>
        <BriefEditorSection
          briefId={workspace._id}
          initialHtml={workspace.overview || ""}
          isDownloading={isDownloading}
          downloadLabel={downloadLabel}
          downloadOpen={downloadOpen}
          statusMessage={statusMessage}
          statusTone={statusTone}
          editorRef={editorRef}
          fileInputRef={fileInputRef}
          downloadMenuRef={downloadMenuRef}
          onToggleDownload={() => setDownloadOpen((v) => !v)}
          onDownloadDoc={handleDownloadDoc}
          onDownloadPdf={handleDownloadPdf}
          onDismissStatus={() => setStatusMessage(null)}
          onEditorChange={handleEditorChange}
          onMediaUploadStart={() => {}}
          onMediaUploadComplete={() => {}}
          onEditorReady={() => {}}
        />
      </section>

      <section className={styles.promptsSection}>
        <h3 className={styles.sectionHeading}>Master prompts</h3>
        <OverviewMasterPrompts workspace={workspace} requests={requests} />
      </section>

      <section>
        <Collapse
          className={styles.requestsCollapse}
          defaultActiveKey={[]}
          items={[
            {
              key: "requests",
              label: (
                <div className={styles.collapseLabel}>
                  <span className={styles.collapseTitle}>Requests</span>
                  <span className={styles.collapseCount}>{requests.length}</span>
                </div>
              ),
              children: (
                requests.length === 0 ? (
                  <div className={styles.emptyRequests}>
                    <p className={styles.emptyTitle}>No requests yet</p>
                    <p className={styles.emptySub}>
                      Set a master prompt above, then start a check from Script Vet or
                      Draft Vet.
                    </p>
                  </div>
                ) : (
                  <div className={styles.requestsList}>
                    {requests.map((item) => {
                      const moduleId = moduleIdForRequestType(item.type);
                      const mod = PROJECT_AI_MODULES.find((m) => m.id === moduleId);
                      const working = requestIsWorking(item, item.type);
                      const { title, subtitle } = requestRowCopy(item);
                      const scriptCreatorCount =
                        item.type === "script-generate"
                          ? creatorsFromRequest(item).length
                          : 0;
                      const chipClass =
                        item.type === "script-vet" || item.type === "script-generate"
                          ? styles.moduleTagScript
                          : item.type === "creator-fit"
                            ? styles.moduleTagCreator
                            : item.type === "draft-vet"
                              ? styles.moduleTagDraft
                              : "";

                      return (
                        <button
                          key={item._id || item.id}
                          type="button"
                          className={styles.requestItem}
                          onClick={() => {
                            onOpenRequest(item._id || item.id, moduleId);
                          }}
                        >
                          <div className={styles.requestInfo}>
                            <div className={styles.requestTags}>
                              <span className={`${styles.moduleTag} ${chipClass}`}>
                                {mod?.title || item.type}
                              </span>
                              {item.type === "script-generate" ? (
                                <span
                                  className={`${styles.moduleTag} ${styles.moduleTagScript}`}
                                >
                                  {scriptCreatorCount}{" "}
                                  {scriptCreatorCount === 1 ? "creator" : "creators"}
                                </span>
                              ) : null}
                              {working && (
                                <span className={styles.workingTag}>Working</span>
                              )}
                              <span className={styles.timeTag}>
                                <Clock size={11} />
                                {item.updatedAt
                                  ? formatDistanceToNow(new Date(item.updatedAt), {
                                      addSuffix: true,
                                    })
                                  : "-"}
                              </span>
                            </div>
                            <p className={styles.requestTitle}>{title}</p>
                            {subtitle && (
                              <p className={styles.requestSubtitle}>{subtitle}</p>
                            )}
                          </div>
                          <ArrowRight size={15} className={styles.requestArrow} />
                        </button>
                      );
                    })}
                  </div>
                )
              ),
            },
          ]}
        />
      </section>
    </div>
  );
};
